import { resolveApiBaseUrl } from '../utils/apiBase';
import { windowRange } from '../utils/window';

const API_BASE = resolveApiBaseUrl();

const defaultWindowParams = () => windowRange(7);

async function graphFetch(path, params = {}) {
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  const response = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'omit',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body?.message) message = body.message;
    } catch {
      // ignore
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

export async function fetchClusters({ limit = 12, from, to } = {}) {
  const window = defaultWindowParams();
  return graphFetch('/graph/clusters', {
    limit,
    from: from || window.from,
    to: to || window.to,
  });
}

export function buildWebTopicUrl(cluster, { days = 7, depth = 2 } = {}) {
  const params = new URLSearchParams();
  const slug = cluster?.slug || cluster?.label;
  const entityId = cluster?.anchorEntityId || cluster?.id;
  if (slug) {
    params.set('q', slug);
  }
  if (entityId) {
    params.set('e', String(entityId));
  }
  params.set('days', String(days));
  params.set('depth', String(depth));
  return `/web?${params.toString()}`;
}

/** Entity-centric `/web` deep link (article sidebar, entity chips). */
export function buildWebEntityUrl(entity, { mode = 'single' } = {}) {
  if (!entity?.id) {
    return '/web';
  }
  const params = new URLSearchParams();
  params.set('mode', mode);
  const slug = entity.slug || entity.label;
  if (slug) {
    params.set('q', slug);
  }
  params.set('e', String(entity.id));
  return `/web?${params.toString()}`;
}

/** Header Web when viewing an article — Constellation expands fromArticle → top-5 `e=`. */
export function buildWebFromArticleUrl(articleId, { layout = 'constellation', mode = 'union' } = {}) {
  if (!articleId) {
    return '/web';
  }
  const params = new URLSearchParams();
  params.set('fromArticle', String(articleId));
  params.set('mode', mode);
  if (layout === 'constellation') {
    params.set('layout', 'constellation');
  }
  return `/web?${params.toString()}`;
}

/** Shareable multi-seed URL with all seed entity IDs in `e`. */
export function buildWebConstellationUrl(entities, { depth = 2, days = 7, mode = 'union' } = {}) {
  const list = (entities || []).filter((entity) => entity?.id).slice(0, 8);
  if (list.length === 0) {
    return '/web?layout=constellation&mode=union';
  }
  const params = new URLSearchParams();
  params.set('mode', mode);
  params.set('layout', 'constellation');
  params.set('e', list.map((entity) => entity.id).join(','));
  const primary = list[0];
  if (primary.slug || primary.label) {
    params.set('q', primary.slug || primary.label);
  }
  params.set('depth', String(depth));
  params.set('days', String(days));
  return `/web?${params.toString()}`;
}

export const SUBGRAPH_PATHS = {
  single: '/graph/subgraph/single',
  union: '/graph/subgraph/union',
  intersect: '/graph/subgraph/intersection',
};

/** Graph search algorithm (Sprint 53). Default single. */
export function parseModeParam(raw) {
  const mode = String(raw || 'single').toLowerCase();
  if (mode === 'union') {
    return 'union';
  }
  if (mode === 'intersect' || mode === 'intersection') {
    return 'intersect';
  }
  return 'single';
}

export const SPRAWL_OPTIONS = [3, 5, 15, 25, 50];

export const DEFAULT_EXPAND_PER_PARENT = 5;

/** Sprawl cap per BFS parent. null/invalid → default 5; 'all' or 0 → unlimited (omit param). */
export function parseExpandPerParentParam(raw) {
  if (raw == null || String(raw).trim() === '') {
    return DEFAULT_EXPAND_PER_PARENT;
  }
  const normalized = String(raw).trim().toLowerCase();
  if (normalized === 'all' || normalized === '0') {
    return 0;
  }
  const n = Number(normalized);
  if (!Number.isFinite(n)) {
    return DEFAULT_EXPAND_PER_PARENT;
  }
  if (n === 0) {
    return 0;
  }
  return SPRAWL_OPTIONS.includes(n) ? n : DEFAULT_EXPAND_PER_PARENT;
}

export function formatExpandPerParentParam(value) {
  if (value == null || value === DEFAULT_EXPAND_PER_PARENT) {
    return null;
  }
  if (value === 0) {
    return 'all';
  }
  return String(value);
}

function normalizeSeedsForMode(mode, { entityId, entityIds, fromArticle, q } = {}) {
  const idsParam = Array.isArray(entityIds)
    ? formatEntityIdsParam(entityIds)
    : (entityIds || undefined);
  const seedList = idsParam ? idsParam.split(',') : [];
  if (mode === 'single') {
    const singleId = seedList[0] || entityId;
    return {
      entityId: singleId || undefined,
      entityIds: undefined,
      fromArticle: !singleId && !q ? fromArticle : undefined,
      q: !singleId && !fromArticle ? q : undefined,
    };
  }
  return {
    entityId: undefined,
    entityIds: idsParam,
    fromArticle: idsParam ? undefined : fromArticle,
    q: idsParam || fromArticle ? undefined : q,
  };
}

export async function fetchSubgraphByMode(
  mode = 'single',
  {
    entityId,
    entityIds,
    fromArticle,
    q,
    depth = 2,
    from,
    to,
    minWeight,
    expandPerParent,
  } = {},
) {
  const window = defaultWindowParams();
  const path = SUBGRAPH_PATHS[parseModeParam(mode)] || SUBGRAPH_PATHS.single;
  const seedParams = normalizeSeedsForMode(parseModeParam(mode), {
    entityId,
    entityIds,
    fromArticle,
    q,
  });
  const sprawl = parseExpandPerParentParam(
    expandPerParent != null ? String(expandPerParent) : String(DEFAULT_EXPAND_PER_PARENT),
  );
  return graphFetch(path, {
    ...seedParams,
    depth,
    from: from || window.from,
    to: to || window.to,
    minWeight,
    expandPerParent: sprawl === 0 ? 0 : sprawl,
  });
}

export function parseEntityIdsFromParam(raw) {
  if (!raw || !String(raw).trim()) {
    return [];
  }
  const seen = new Set();
  const ids = [];
  String(raw).split(',').forEach((part) => {
    const id = part.trim();
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    ids.push(id);
  });
  return ids.slice(0, 8);
}

export function formatEntityIdsParam(ids) {
  const list = (ids || []).map((id) => String(id).trim()).filter(Boolean);
  if (list.length === 0) {
    return null;
  }
  return [...new Set(list)].slice(0, 8).join(',');
}

/** Q5 scoped rail: hop ascending, then salience descending. */
export function rankNeighborhoodEntities(nodes, { limit = 8 } = {}) {
  const list = (nodes || []).slice();
  list.sort((a, b) => {
    const hopA = a.hop ?? 99;
    const hopB = b.hop ?? 99;
    if (hopA !== hopB) {
      return hopA - hopB;
    }
    return (b.salience ?? 0) - (a.salience ?? 0);
  });
  return list.slice(0, limit).map((node) => ({
    id: node.id,
    label: node.name || node.label,
    slug: node.slug,
    type: node.type,
    articleCount: node.articleCount ?? 0,
    hop: node.hop,
    salience: node.salience ?? 0,
  }));
}

export function parseLayoutParam(raw) {
  return String(raw || '').toLowerCase() === 'constellation' ? 'constellation' : 'starburst';
}

/** Entity | Story hybrid (MS-VIZ-03). Default entity. */
export function parseViewModeParam(raw) {
  return String(raw || '').toLowerCase() === 'story' ? 'story' : 'entity';
}

/**
 * 2D | 3D render (MS-VIZ-03). Default 2d.
 * Compat: legacy callers may pass view=3d — treat as render=3d when render is unset.
 */
export function parseRenderParam(raw, { legacyView } = {}) {
  const render = String(raw || '').toLowerCase();
  if (render === '3d') {
    return '3d';
  }
  if (render === '2d') {
    return '2d';
  }
  if (String(legacyView || '').toLowerCase() === '3d') {
    return '3d';
  }
  return '2d';
}

/** MS-RAIL-02: keep clusters whose anchor is in the visible neighborhood (incl. seeds). */
export function filterClustersByNeighborhood(clusters, visibleEntityIds) {
  const ids = visibleEntityIds instanceof Set
    ? visibleEntityIds
    : new Set((visibleEntityIds || []).map((id) => String(id)));
  if (ids.size === 0) {
    return [];
  }
  return (clusters || []).filter((cluster) => {
    const anchor = cluster?.anchorEntityId ?? cluster?.id;
    return anchor != null && ids.has(String(anchor));
  });
}

/**
 * Bipartite Story graph (MS-VIZ-04): article nodes + entity nodes; Mentions edges only.
 * Articles ranked by sum of matched-entity salience, then publishedAt; capped by topN (Density).
 */
export function buildBipartiteStoryGraph({
  entityNodes = [],
  articles = [],
  topN = null,
} = {}) {
  const entities = (entityNodes || []).map((node) => ({
    id: String(node.id),
    name: node.name || node.label || 'Entity',
    type: node.type || 'TOPIC',
    hop: node.hop ?? 1,
    articleCount: node.articleCount ?? 0,
    salience: node.salience ?? 0,
    nodeKind: 'entity',
  }));
  const entityIds = new Set(entities.map((node) => node.id));
  const salienceById = new Map(entities.map((node) => [node.id, node.salience ?? 0]));

  const ranked = (articles || [])
    .map((article) => {
      const matched = (article.matchedEntities || [])
        .map((id) => String(id))
        .filter((id) => entityIds.has(id));
      if (matched.length === 0) {
        return null;
      }
      const salience = matched.reduce(
        (sum, id) => sum + (salienceById.get(id) ?? 0),
        0,
      );
      const publishedAt = article.publishedAt ? Date.parse(article.publishedAt) : 0;
      return {
        id: `article:${article.id}`,
        articleId: String(article.id),
        name: article.title || 'Untitled',
        type: 'ARTICLE',
        hop: 0,
        articleCount: 1,
        salience,
        publishedAt: Number.isFinite(publishedAt) ? publishedAt : 0,
        matchedEntities: matched,
        nodeKind: 'article',
        source: article.source,
        category: article.category,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (b.salience !== a.salience) {
        return b.salience - a.salience;
      }
      return b.publishedAt - a.publishedAt;
    });

  const cap = topN != null && Number.isFinite(Number(topN)) ? Number(topN) : null;
  const articleNodes = cap != null && cap > 0 ? ranked.slice(0, cap) : ranked;

  const links = [];
  const linkKeys = new Set();
  articleNodes.forEach((article) => {
    article.matchedEntities.forEach((entityId) => {
      const key = `${article.id}|${entityId}`;
      if (linkKeys.has(key)) {
        return;
      }
      linkKeys.add(key);
      links.push({
        source: article.id,
        target: entityId,
        kind: 'MENTIONS',
        predicate: 'mentions',
        relationType: null,
        modality: null,
        weight: 1,
      });
    });
  });

  // Keep entities that are seeds/hop0 or linked to a visible article.
  const linkedEntityIds = new Set();
  links.forEach((link) => {
    linkedEntityIds.add(String(link.target));
  });
  const visibleEntities = entities.filter((node) => (
    (node.hop ?? 1) === 0 || linkedEntityIds.has(node.id)
  ));

  return {
    nodes: [...articleNodes, ...visibleEntities],
    links,
    stats: {
      totalArticles: ranked.length,
      visibleArticles: articleNodes.length,
      visibleEntities: visibleEntities.length,
      visibleLinks: links.length,
    },
  };
}

/** MS-VIZ-05: map article salience in [minS, maxS] → canvas alpha in [minAlpha, 1]. */
export function articleSalienceAlpha(
  salience,
  minS,
  maxS,
  { minAlpha = 0.5, rankIndex = null, rankCount = null } = {},
) {
  if (!Number.isFinite(salience)) {
    return 1;
  }
  if (maxS <= minS) {
    if (rankCount != null && rankCount > 1 && rankIndex != null) {
      const t = rankIndex / (rankCount - 1);
      return minAlpha + (1 - minAlpha) * t;
    }
    return 1;
  }
  const t = (salience - minS) / (maxS - minS);
  const clamped = Math.max(0, Math.min(1, t));
  return minAlpha + (1 - minAlpha) * clamped;
}

/** MS-VIZ-05: paint low-salience articles first so higher salience stacks on top. */
export function orderStoryNodesForPaint(nodes = []) {
  const articles = [];
  const entities = [];
  (nodes || []).forEach((node) => {
    if (node.nodeKind === 'article') {
      articles.push(node);
    } else {
      entities.push(node);
    }
  });
  articles.sort((a, b) => {
    const sa = a.salience ?? 0;
    const sb = b.salience ?? 0;
    if (sa !== sb) {
      return sa - sb;
    }
    return (a.publishedAt ?? 0) - (b.publishedAt ?? 0);
  });
  return [...articles, ...entities];
}

/** Min/max salience among visible article nodes (for alpha normalization). */
export function storyArticleSalienceRange(nodes = []) {
  const saliences = (nodes || [])
    .filter((node) => node.nodeKind === 'article')
    .map((node) => node.salience ?? 0);
  if (saliences.length === 0) {
    return { min: 0, max: 0 };
  }
  return {
    min: Math.min(...saliences),
    max: Math.max(...saliences),
  };
}

export async function fetchTrendingEntities({ limit = 8, from, to } = {}) {
  const window = defaultWindowParams();
  return graphFetch('/graph/entities/trending', {
    limit,
    from: from || window.from,
    to: to || window.to,
  });
}

export async function searchGraphEntities(query, { type, limit = 20, from, to } = {}) {
  const window = defaultWindowParams();
  return graphFetch('/graph/entities', {
    q: query,
    type,
    limit,
    from: from || window.from,
    to: to || window.to,
  });
}

export async function fetchSubgraph({
  entityId,
  entityIds,
  fromArticle,
  q,
  depth = 2,
  from,
  to,
  minWeight,
  expandPerParent,
} = {}) {
  return fetchSubgraphByMode('single', {
    entityId,
    entityIds,
    fromArticle,
    q,
    depth,
    from,
    to,
    minWeight,
    expandPerParent,
  });
}

export async function fetchEntityArticles(entityId, { page = 0, size = 25, from, to, sort = 'recent' } = {}) {
  const window = defaultWindowParams();
  return graphFetch(`/graph/entities/${entityId}/articles`, {
    page,
    size,
    sort,
    from: from || window.from,
    to: to || window.to,
  });
}

export async function fetchEntityBySlug(slug, { from, to } = {}) {
  const window = defaultWindowParams();
  return graphFetch(`/graph/entities/by-slug/${encodeURIComponent(slug)}`, {
    from: from || window.from,
    to: to || window.to,
  });
}

export async function fetchArticleEntities(articleId, { limit = 8 } = {}) {
  return graphFetch(`/graph/articles/${encodeURIComponent(articleId)}/entities`, { limit });
}

/** Article ids (subset of input) that have ≥1 graph entity link — Desk Web-chip gate. */
export async function fetchArticleIdsWithEntities(articleIds) {
  const ids = [...new Set((articleIds || []).map((id) => String(id || '').trim()).filter(Boolean))].slice(
    0,
    300,
  );
  if (ids.length === 0) {
    return [];
  }
  const result = await graphFetch('/graph/articles/with-entities', { ids: ids.join(',') });
  return Array.isArray(result) ? result : [];
}

export async function fetchRelatedArticles(articleId, { limit = 6 } = {}) {
  return graphFetch(`/graph/articles/${encodeURIComponent(articleId)}/related`, { limit });
}

export async function fetchRelatedEntities(entityId, { limit = 8, from, to } = {}) {
  const window = defaultWindowParams();
  return graphFetch(`/graph/entities/${entityId}/related`, {
    limit,
    from: from || window.from,
    to: to || window.to,
  });
}

function linkEndpointId(endpoint) {
  if (endpoint == null) {
    return null;
  }
  return String(typeof endpoint === 'object' ? endpoint.id : endpoint);
}

export const ENTITY_TYPES = [
  'PERSON',
  'ORG',
  'GPE',
  'EVENT',
  'LAW',
  'COMMODITY',
  'TICKER',
  'TOPIC',
];

/** Count visible graph nodes per ENTITY_TYPES key (MS-VIZ-06). */
export function countNodesByEntityType(nodes, { excludeTypes = [] } = {}) {
  const exclude = new Set(excludeTypes.map((type) => String(type).toUpperCase()));
  const counts = new Map(ENTITY_TYPES.map((type) => [type, 0]));
  (nodes || []).forEach((node) => {
    const type = String(node?.type || '').toUpperCase();
    if (!type || exclude.has(type)) {
      return;
    }
    if (ENTITY_TYPES.includes(type)) {
      counts.set(type, (counts.get(type) || 0) + 1);
    }
  });
  return counts;
}

export const DEFAULT_MIN_SALIENCE = 0.3;
export const LINK_KIND_OPTIONS = ['RELATED', 'CO_MENTION'];
export const RELATION_TYPE_OPTIONS = [
  'CAUSAL',
  'TEMPORAL',
  'SUBEVENT',
  'ROLE',
  'SPEECH',
  'ACTION',
  'AFFILIATION',
  'OTHER',
];
export const TOP_N_OPTIONS = [50, 100, 150];
export const DEFAULT_TOP_N = 50;
/** Zoom scale above which RELATED edge labels paint without hover (MS-VIZ-02). */
export const EDGE_LABEL_ZOOM = 1.15;

export function parseEntityTypesParam(typesParam) {
  if (!typesParam || !String(typesParam).trim()) {
    return new Set(ENTITY_TYPES);
  }
  const selected = String(typesParam)
    .split(',')
    .map((value) => value.trim().toUpperCase())
    .filter((value) => ENTITY_TYPES.includes(value));
  return selected.length > 0 ? new Set(selected) : new Set(ENTITY_TYPES);
}

export function formatEntityTypesParam(selectedTypes) {
  const selected = [...selectedTypes].sort();
  if (selected.length === 0 || selected.length === ENTITY_TYPES.length) {
    return null;
  }
  return selected.join(',');
}

/** Toggle one entity type. Refuses to drop the last remaining type. */
export function toggleEntityType(selectedTypes, type) {
  const next = new Set(selectedTypes);
  if (!ENTITY_TYPES.includes(type)) {
    return next;
  }
  if (next.has(type)) {
    if (next.size <= 1) {
      return next;
    }
    next.delete(type);
  } else {
    next.add(type);
  }
  return next;
}

export function parseMinSalienceParam(minSalienceParam) {
  if (minSalienceParam == null || minSalienceParam === '') {
    return DEFAULT_MIN_SALIENCE;
  }
  const parsed = Number(minSalienceParam);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_MIN_SALIENCE;
  }
  return Math.min(1, Math.max(0, parsed));
}

/** null = All link kinds */
export function parseLinkKindParam(raw) {
  if (raw == null || raw === '' || String(raw).toLowerCase() === 'all') {
    return null;
  }
  const kind = String(raw).trim().toUpperCase();
  return LINK_KIND_OPTIONS.includes(kind) ? kind : null;
}

/** null = All relation types (MS-EC-04a) */
export function parseRelationTypeParam(raw) {
  if (raw == null || raw === '' || String(raw).toLowerCase() === 'all') {
    return null;
  }
  const type = String(raw).trim().toUpperCase();
  return RELATION_TYPE_OPTIONS.includes(type) ? type : null;
}

/** null = All (no top-N cap); omitted URL → DEFAULT_TOP_N */
export function parseTopNParam(raw) {
  if (raw == null || raw === '') {
    return DEFAULT_TOP_N;
  }
  if (String(raw).toLowerCase() === 'all') {
    return null;
  }
  const n = parseInt(raw, 10);
  return TOP_N_OPTIONS.includes(n) ? n : DEFAULT_TOP_N;
}

export function formatTopNParam(topN) {
  if (topN == null) {
    return 'all';
  }
  if (topN === DEFAULT_TOP_N) {
    return null;
  }
  return String(topN);
}

export function normalizeModality(link) {
  const modality = String(link?.modality || '').trim().toLowerCase();
  if (modality === 'asserted' || modality === 'alleged' || modality === 'speculative') {
    return modality;
  }
  return null;
}

/** Product language: typed edges are claims in coverage, not proven world causality. */
export function formatModalityCopy(modality) {
  if (modality === 'asserted') {
    return 'asserted in source';
  }
  if (modality === 'alleged') {
    return 'alleged in source';
  }
  if (modality === 'speculative') {
    return 'speculative in source';
  }
  return '';
}

export function linkStrokeStyle(link) {
  const kind = link?.kind || '';
  if (kind !== 'RELATED') {
    return { color: 'rgba(200,214,229,0.35)', dash: [] };
  }
  const modality = normalizeModality(link);
  if (modality === 'asserted') {
    return { color: 'rgba(77,163,255,0.82)', dash: [] };
  }
  if (modality === 'alleged') {
    return { color: 'rgba(77,163,255,0.48)', dash: [7, 4] };
  }
  if (modality === 'speculative') {
    return { color: 'rgba(125,163,210,0.38)', dash: [2, 4] };
  }
  return { color: 'rgba(77,163,255,0.55)', dash: [] };
}

/**
 * Edge label for RELATED / CO_MENTION (MS-VIZ-02).
 * Zoom chrome shows predicate only; detailed hover may include weight + modality.
 * relationType stays on the link for Filters — not painted on the canvas.
 */
export function formatLinkLabel(link, { detailed = false } = {}) {
  if (!link) {
    return '';
  }
  const kind = link.kind || 'LINK';
  const weight = link.weight != null ? Number(link.weight).toFixed(2) : null;
  const modalityCopy = detailed ? formatModalityCopy(normalizeModality(link)) : '';
  if (kind === 'RELATED') {
    const predicate = link.predicate ? String(link.predicate) : 'related';
    if (detailed && weight != null) {
      return modalityCopy ? `${predicate} · w=${weight} · ${modalityCopy}` : `${predicate} · w=${weight}`;
    }
    if (detailed && modalityCopy) {
      return `${predicate} · ${modalityCopy}`;
    }
    return predicate;
  }
  if (detailed && weight != null) {
    return modalityCopy ? `CO_MENTION · w=${weight} · ${modalityCopy}` : `CO_MENTION · w=${weight}`;
  }
  if (detailed && modalityCopy) {
    return `CO_MENTION · ${modalityCopy}`;
  }
  return 'CO_MENTION';
}

/**
 * Client-side graph distillation: type → min salience → link kind → relationType → top-N → canvas.
 * All seed nodes are always kept when present.
 */
export function distillGraphData(graphData, {
  centerId = null,
  seedIds = null,
  types = null,
  minSalience = DEFAULT_MIN_SALIENCE,
  linkKind = null,
  relationType = null,
  topN = null,
} = {}) {
  const nodes = graphData?.nodes || [];
  const links = graphData?.links || [];
  const seedKeys = new Set(
    (seedIds && seedIds.length > 0
      ? seedIds
      : (centerId != null ? [centerId] : [])
    ).map((id) => String(id)),
  );
  const typeSet = types instanceof Set ? types : null;
  const floor = parseMinSalienceParam(minSalience);
  const kindFilter = linkKind ? String(linkKind).toUpperCase() : null;
  const relationFilter = relationType ? String(relationType).toUpperCase() : null;
  const cap = topN != null && Number.isFinite(Number(topN)) ? Number(topN) : null;

  const keepNode = (node) => {
    if (seedKeys.has(String(node.id))) {
      return true;
    }
    if (typeSet && typeSet.size > 0 && !typeSet.has(String(node.type || '').toUpperCase())) {
      return false;
    }
    const salience = node.salience ?? 0;
    return salience >= floor;
  };

  let visibleNodes = nodes.filter(keepNode);
  let visibleNodeIds = new Set(visibleNodes.map((node) => String(node.id)));

  let visibleLinks = links.filter((link) => {
    const source = linkEndpointId(link.source);
    const target = linkEndpointId(link.target);
    if (!visibleNodeIds.has(source) || !visibleNodeIds.has(target)) {
      return false;
    }
    if (kindFilter && String(link.kind || '').toUpperCase() !== kindFilter) {
      return false;
    }
    if (relationFilter) {
      // relationType only applies to RELATED edges; CO_MENTION has null type.
      if (String(link.kind || '').toUpperCase() !== 'RELATED') {
        return false;
      }
      const linkType = link.relationType
        ? String(link.relationType).toUpperCase()
        : 'OTHER';
      if (linkType !== relationFilter) {
        return false;
      }
    }
    return true;
  });

  // Drop orphan nodes after link-kind / relationType filter (seeds always kept).
  if (kindFilter || relationFilter) {
    const linkedIds = new Set();
    visibleLinks.forEach((link) => {
      linkedIds.add(linkEndpointId(link.source));
      linkedIds.add(linkEndpointId(link.target));
    });
    visibleNodes = visibleNodes.filter((node) => {
      const id = String(node.id);
      if (seedKeys.has(id)) return true;
      return linkedIds.has(id);
    });
    visibleNodeIds = new Set(visibleNodes.map((node) => String(node.id)));
    visibleLinks = visibleLinks.filter((link) => (
      visibleNodeIds.has(linkEndpointId(link.source))
      && visibleNodeIds.has(linkEndpointId(link.target))
    ));
  }

  // Top-N by articleCount among non-seed neighbors (seeds always kept).
  if (cap != null && cap > 0) {
    const seedNodes = visibleNodes.filter((node) => seedKeys.has(String(node.id)));
    const others = visibleNodes
      .filter((node) => !seedKeys.has(String(node.id)))
      .sort((a, b) => (b.articleCount || 0) - (a.articleCount || 0))
      .slice(0, cap);
    visibleNodes = [...seedNodes, ...others];
    visibleNodeIds = new Set(visibleNodes.map((node) => String(node.id)));
    visibleLinks = visibleLinks.filter((link) => (
      visibleNodeIds.has(linkEndpointId(link.source))
      && visibleNodeIds.has(linkEndpointId(link.target))
    ));
  }

  return {
    nodes: visibleNodes,
    links: visibleLinks,
    stats: {
      totalNodes: nodes.length,
      visibleNodes: visibleNodes.length,
      totalLinks: links.length,
      visibleLinks: visibleLinks.length,
    },
  };
}

export function subgraphToForceGraph(subgraph) {
  if (!subgraph) {
    return { nodes: [], links: [] };
  }

  const nodes = (subgraph.nodes || []).map((node) => ({
    id: node.id,
    name: node.label,
    type: node.type,
    hop: node.hop,
    articleCount: node.articleCount,
    salience: node.salience ?? 0,
  }));

  const links = (subgraph.links || []).map((link) => ({
    source: link.source,
    target: link.target,
    kind: link.kind,
    predicate: link.predicate,
    relationType: link.relationType ?? null,
    modality: link.modality ?? null,
    weight: link.weight,
  }));

  return { nodes, links };
}

/** Clone graph data for canvas renderers — strips prior force-sim coordinates. */
export function cloneForceGraphData(graphData) {
  if (!graphData) {
    return { nodes: [], links: [] };
  }

  const nodes = (graphData.nodes || []).map((node) => ({
    id: node.id,
    name: node.name,
    type: node.type,
    hop: node.hop,
    articleCount: node.articleCount,
    salience: node.salience ?? 0,
    publishedAt: node.publishedAt ?? 0,
    nodeKind: node.nodeKind || null,
    articleId: node.articleId || null,
  }));

  const nodeIds = new Set(nodes.map((node) => String(node.id)));
  const links = (graphData.links || [])
    .map((link) => ({
      source: linkEndpointId(link.source),
      target: linkEndpointId(link.target),
      kind: link.kind,
      predicate: link.predicate,
      relationType: link.relationType ?? null,
      modality: link.modality ?? null,
      weight: link.weight,
    }))
    .filter((link) => nodeIds.has(link.source) && nodeIds.has(link.target));

  return { nodes, links };
}

/** Seed hop rings on a sphere so hub-and-spoke subgraphs do not collapse in 3D. */
export function seedSphericalLayout(nodes) {
  const byHop = new Map();
  nodes.forEach((node) => {
    const hop = node.hop ?? 0;
    if (!byHop.has(hop)) {
      byHop.set(hop, []);
    }
    byHop.get(hop).push(node);
  });

  byHop.forEach((hopNodes, hop) => {
    if (hop === 0) {
      const [center] = hopNodes;
      if (center) {
        center.x = 0;
        center.y = 0;
        center.z = 0;
      }
      return;
    }

    const radius = 55 + Number(hop) * 45;
    const count = hopNodes.length;
    hopNodes.forEach((node, index) => {
      const phi = Math.acos(-1 + (2 * (index + 0.5)) / count);
      const theta = Math.PI * (1 + Math.sqrt(5)) * index;
      node.x = radius * Math.sin(phi) * Math.cos(theta);
      node.y = radius * Math.sin(phi) * Math.sin(theta);
      node.z = radius * Math.cos(phi);
    });
  });
}

export function prepareGraphDataFor3D(graphData) {
  const cloned = cloneForceGraphData(graphData);
  if (cloned.nodes.length > 0) {
    seedSphericalLayout(cloned.nodes);
  }
  return cloned;
}

/**
 * Radial "orbit" force: pulls each node toward a ring whose radius is set by its
 * hop distance from center (hop 0 → origin, hop 1 → ringRadius, hop 2 → 2×, …).
 * This makes distance-from-center represent graph depth so a single weakly
 * connected outlier can no longer drift far out. Works for 2D and 3D nodes
 * (updates vz only when a node carries a z coordinate).
 */
export function createRadialHopForce({
  ringRadius = 90,
  strength = 0.35,
  center = { x: 0, y: 0, z: 0 },
} = {}) {
  let nodes = [];
  function force(alpha) {
    const k = strength * alpha;
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      const targetR = Math.max(node.hop ?? 1, 0) * ringRadius;
      const dx = (node.x ?? 0) - center.x;
      const dy = (node.y ?? 0) - center.y;
      const is3d = node.z != null;
      const dz = is3d ? node.z - center.z : 0;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-6;
      const pull = ((targetR - dist) / dist) * k;
      node.vx = (node.vx ?? 0) + dx * pull;
      node.vy = (node.vy ?? 0) + dy * pull;
      if (is3d) {
        node.vz = (node.vz ?? 0) + dz * pull;
      }
    }
  }
  force.initialize = (input) => {
    nodes = input;
  };
  return force;
}

/**
 * Multi-seed radial rings (MS-SEED-03): hop-0 seeds sit on a small ring (not
 * stacked at origin); hop N → N × ringRadius. Shared by Constellation + multi-seed Starburst.
 */
export function createMultiSeedRadialForce({
  ringRadius = 90,
  seedRing = 48,
  strength = 0.35,
} = {}) {
  let nodes = [];
  function force(alpha) {
    const k = strength * alpha;
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      const hop = node.hop ?? 1;
      const targetR = hop === 0 ? seedRing : hop * ringRadius;
      const dx = (node.x ?? 0);
      const dy = (node.y ?? 0);
      const dist = Math.sqrt(dx * dx + dy * dy) || 1e-6;
      const pull = ((targetR - dist) / dist) * k;
      node.vx = (node.vx ?? 0) + dx * pull;
      node.vy = (node.vy ?? 0) + dy * pull;
    }
  }
  force.initialize = (input) => {
    nodes = input;
  };
  return force;
}

/** Spread hop-0 seeds on a ring so multi-seed layouts do not start stacked. */
export function seedMultiSeedRingPositions(nodes, seedRing = 48) {
  const seeds = (nodes || []).filter((node) => (node.hop ?? 0) === 0);
  seeds.forEach((node, index) => {
    const angle = (2 * Math.PI * index) / Math.max(seeds.length, 1);
    node.x = Math.cos(angle) * seedRing;
    node.y = Math.sin(angle) * seedRing;
  });
  return seeds.length;
}

/**
 * Log-damped link length: higher edge weight pulls endpoints slightly closer,
 * but on a log scale so a single very-high-weight co-mention cannot collapse or
 * blow out the distance. Radius is driven mainly by hop (createRadialHopForce);
 * link length only nudges angular/local spacing.
 */
export function linkDistanceForWeight(weight, { base = 46, min = 18, damp = 14 } = {}) {
  const w = Math.max(weight || 1, 1);
  return Math.max(min, base - Math.log10(w + 1) * damp);
}

export const ENTITY_TYPE_COLORS = {
  ORG: '#4da3ff',
  PERSON: '#5fd38d',
  GPE: '#f0b429',
  EVENT: '#ff6b6b',
  LAW: '#b388ff',
  COMMODITY: '#ff9f43',
  TICKER: '#48dbfb',
  TOPIC: '#c8d6e5',
};

/** Ring drawn around nodes focused from outside the canvas (timeline hover). */
export const FOCUS_RING_COLOR = '#ffd166';
